import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';
import { ContactInfo } from '../../Utils/Data.jsx';

// Helper function to normalize phone numbers to array
const normalizePhoneNumbers = (data) => {
  if (!data) {
    return { contact_phone: [] };
  }
  const normalized = { ...data };
  
  if (normalized.contact_phone === undefined || normalized.contact_phone === null) {
    normalized.contact_phone = [];
  } else if (Array.isArray(normalized.contact_phone)) {
    normalized.contact_phone = normalized.contact_phone
      .map(p => {
        if (p === null || p === undefined) return null;
        if (typeof p === 'object') {
          const values = Object.values(p).filter(v => v && typeof v === 'string');
          return values.length > 0 ? values[0] : null;
        }
        const str = String(p);
        if (str === '[object Object]' || str.includes('[object')) return null;
        return str;
      })
      .filter(p => p && p.trim() !== '' && p !== '[object Object]' && !String(p).includes('[object'));
  } else if (typeof normalized.contact_phone === 'string') {
    try {
      const parsed = JSON.parse(normalized.contact_phone);
      if (Array.isArray(parsed)) {
        normalized.contact_phone = parsed
          .map(p => String(p))
          .filter(p => p && p.trim() !== '');
      } else if (typeof parsed === 'string') {
        normalized.contact_phone = [parsed];
      } else {
        normalized.contact_phone = [];
      }
    } catch (e) {
      normalized.contact_phone = [normalized.contact_phone];
    }
  } else {
    normalized.contact_phone = [];
  }

  if (!Array.isArray(normalized.contact_phone)) {
    normalized.contact_phone = [];
  }

  return normalized;
};

// Async thunks
export const fetchContactInfo = createAsyncThunk(
  'contactInfo/fetchContactInfo',
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiService.getContactInfo();
      return normalizePhoneNumbers(data || ContactInfo);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateContactInfoAsync = createAsyncThunk(
  'contactInfo/updateContactInfo',
  async (contactInfo, { rejectWithValue }) => {
    try {
      const normalized = normalizePhoneNumbers(contactInfo);
      const data = await apiService.updateContactInfo(normalized);
      return normalizePhoneNumbers(data);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const contactInfoSlice = createSlice({
  name: 'contactInfo',
  initialState: {
    data: normalizePhoneNumbers(ContactInfo),
    loading: false,
    error: null,
  },
  reducers: {
    setContactInfo: (state, action) => {
      state.data = normalizePhoneNumbers(action.payload);
    },
    updateContactInfo: (state, action) => {
      state.data = normalizePhoneNumbers(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContactInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContactInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.data = normalizePhoneNumbers(action.payload);
      })
      .addCase(fetchContactInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateContactInfoAsync.fulfilled, (state, action) => {
        state.data = normalizePhoneNumbers(action.payload);
      });
  },
});

export const { setContactInfo, updateContactInfo } = contactInfoSlice.actions;
export default contactInfoSlice.reducer;

